import PageLayout from '../../components/PageLayout';
import useSWR from 'swr';
import { useHistory } from 'react-router-dom';
import {
  formatCurrencyV2,
  formatDate,
  getQueryString,
  makeDropdownOptions,
  OPTIONS,
  useReducerSpread,
} from '../../utils/common.util';
import { StatsBar } from '../../components/PrintOutHead';
import { Select, SelectItem, TextInput } from 'carbon-components-react';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { useEffect, useState } from 'react';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { postMethod } from '../../api/common';
import { useMemo } from 'react';
import { ConfirmModal } from '../../components/modal/confirm-modal/ConfirmModal.component';
import {
  AdditionalStudents,
  calculatePaymentBreakdown,
} from './collect-daily-fees.page';
import { useQueryParams } from '../../utils/useQueryParams';
import { StudentSearchField } from '../../components/student-search/StudentSearchField';
import { filterStudentsBySearchTerm } from '../../utils/student-search.util';
import { useClassAccessList } from '../../api/class.service';

export function EditDailyFeesPage() {
  const history = useHistory();

  const {
    dailyBillingFeeId: urlDailyBillingFeeId,
    dateFrom: urlPaymentDate,
    // debitAccountId: urlDebitAccountId,
    classId: urlClassId,
    genericGroupingId: urlGenericGroupingId,
  } = useQueryParams(
    'dailyBillingFeeId',
    'classId',
    'dateFrom',
    'debitAccountId',
    'genericGroupingId',
  );

  // if (!urlPaymentDate || !urlClassId || !urlDailyBillingFeeId ) {
  //   // history.push('/daily-fees');
  // }

  const [formData, setFormData] = useReducerSpread({
    ledgerAccountId: null,
    revenues: [],
  });

  const {
    data: paymentsData,
    isLoading: isLoadingPayments,
    // error,
  } = useSWR(
    urlDailyBillingFeeId && urlPaymentDate
      ? `daily-billing/revenue/list?${getQueryString({
          classId: urlClassId,
          genericGroupingId: urlGenericGroupingId,
          dailyBillingFeeId: urlDailyBillingFeeId,
          dateFrom: urlPaymentDate,
        })}`
      : null,
  );
  const responseData = paymentsData?.payment ?? null;
  const feeType = responseData?.dailyBillingFee ?? null;
  const debitAccount = responseData?.debitAccount ?? null;
  const revenues = useMemo(() => responseData?.revenues ?? [], [responseData]);

  const group = responseData?.genericGroupingAmount ?? null;
  const currentAmount = responseData?.classAmount?.amount ?? group?.amount;

  useEffect(() => {
    if (revenues.length > 0) {
      setFormData({
        ledgerAccountId: feeType?.ledgerAccount?.id,
        debitAccountId: debitAccount?.id,
        revenues: revenues.map((entry) => {
          const totalArrears = entry.arrearsBalance || 0;
          const depositBalance = entry.depositBalance || 0;
          const totalPayment = entry.amountPaid || 0;

          const initialPayment = {
            id: entry?.id,
            studentId: entry?.student?.id,
            uniqueId: entry?.student?.studentId,
            firstName: entry?.student?.firstName || '',
            lastName: entry?.student?.lastName || '',
            class: entry?.student?.classDescription || '',
            attendanceStatus: entry?.attendanceStatus,
            depositPaymentAmount: entry?.depositPaymentAmount,
            // temporary store of original amountPaid when amountPaid is changed
            tempAmountPaid: totalPayment,
            depositBalance,
            totalPayment,
            studentBillId: entry?.studentBillId,
            // if payment mode is not in the OPTIONS.present list, default to empty string
            paymentMode: OPTIONS.paymentModes.find(
              (mode) => mode.value === entry?.paymentMode,
            )
              ? entry?.paymentMode
              : '',
          };

          return {
            ...calculatePaymentBreakdown({
              totalPayment,
              totalArrears,
              currentAmount,
              depositBalance,
              attendanceStatus: entry?.paymentMode,
            }),
            ...initialPayment,
          };
        }),
      });
    }
  }, [currentAmount, setFormData, revenues, feeType, debitAccount]);

  const { trigger, isMutating, error } = useCustomMutation(
    `daily-billing/revenue/new/v2`,
    (url, { arg }) => postMethod(url, arg),
  );

  const { classAccessList } = useClassAccessList();

  const handleSubmit = async (e) => {
    e.preventDefault();
    trigger(
      {
        ...formData,
        paymentDate: urlPaymentDate,
        genericGroupingId: urlGenericGroupingId,
        classId: urlClassId,
        dailyBillingFeeId: urlDailyBillingFeeId,
        debitAccountId: debitAccount?.id,
      },
      {
        onSuccess: () => {
          setIsSaved(true);
          setHasChanged(false);
        },
      },
    );
  };

  const [isOpen, setIsOpen] = useState('');
  const {
    trigger: submit,
    isMutating: isSubmitting,
    error: submissionError,
  } = useCustomMutation(`daily-billing/revenue/approve/v2`, (url, { arg }) =>
    postMethod(url, arg),
  );

  const [hasChanged, setHasChanged] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [classSearchTerm, setClassSearchTerm] = useState('');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('');

  const { data: s, isLoading: isLoadingAdditionalStudents } = useSWR(
    `daily-billing/fees/assigned-students?${getQueryString({
      dailyBillingFeeId: urlDailyBillingFeeId,
      assignedToDailyBillingFee: false,
      classId: urlClassId,
      genericGroupingId: urlGenericGroupingId,
    })}`,
  );
  const additionalStudents =
    s?.response?.map((student) => ({
      studentId: student.id,
      uniqueId: student.studentId,
      firstName: student.firstName,
      lastName: student.lastName,
      classDescription: student.classDescription,
      attendanceStatus: 'Present',
      depositPaymentAmount: 0,
      amountPaid: currentAmount || 0,
      studentBillId: null,
      paymentMode: 'Cash',
    })) ?? [];

  const visibleRevenues = useMemo(() => {
    let results = filterStudentsBySearchTerm(
      formData.revenues,
      studentSearchTerm,
    );
    if (classSearchTerm) {
      const selectedClass = classAccessList?.find(
        (c) => String(c.id) === String(classSearchTerm),
      );
      if (selectedClass) {
        results = results.filter(
          (item) => item.class === selectedClass.description,
        );
      }
    }
    if (attendanceStatusFilter) {
      results = results.filter(
        (item) => item.attendanceStatus === attendanceStatusFilter,
      );
    }
    return results;
  }, [
    formData.revenues,
    studentSearchTerm,
    classSearchTerm,
    attendanceStatusFilter,
    classAccessList,
  ]);

  const statsItems = [
    ...(feeType?.type === 'Standard'
      ? [{ label: 'Class', value: feeType?.class?.description }]
      : [{ label: 'Group', value: group?.name }]),
    { label: 'Amount', value: formatCurrencyV2(currentAmount) },
    {
      label: 'Debit Account',
      value: debitAccount?.description,
    },
    { label: 'Payment Date', value: formatDate(urlPaymentDate) },
  ];
  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: `Edit Daily Fees Draft`, active: true },
      ]}
      title={`Edit ${feeType?.name || 'Daily Fee'} Draft`}
      className="fee-type-page"
      isLoading={isLoadingPayments || isLoadingAdditionalStudents}
    >
      <div style={{ borderBottom: '1px solid #ccc' }}>
        <StatsBar statsItems={statsItems} />
      </div>

      {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
      {formData.revenues?.length === 0 && (
        <p className="text-14 mt-30 text-grey">
          No data available for draft update.
        </p>
      )}
      {formData.revenues?.length > 0 && (
        <form onSubmit={handleSubmit}>
          <div
            className="d-flex gap-10 mt-20"
            style={{ alignItems: 'flex-end' }}
          >
            {feeType?.type === 'Generic' && (
              <div>
                <Select
                  style={{ minWidth: '200px' }}
                  labelText="Class"
                  onChange={(e) => setClassSearchTerm(e.target.value)}
                  value={classSearchTerm}
                >
                  <SelectItem value="" text="All" />
                  {classAccessList?.map((item) => (
                    <SelectItem
                      key={item.id}
                      value={item.id}
                      text={item.description}
                    />
                  ))}
                </Select>
              </div>
            )}
            <div className="mt-30" style={{ flex: 1 }}>
              <StudentSearchField
                id="edit-daily-fees-search"
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                placeholder="Search by student name"
              />
            </div>
            <div>
              <Select
                style={{ minWidth: '200px' }}
                labelText="Attendance"
                onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                value={attendanceStatusFilter}
              >
                <SelectItem value="" text="All" />
                {makeDropdownOptions(['Present', 'Absent']).map((item) => (
                  <SelectItem
                    key={item.value}
                    text={item.label}
                    value={item.value}
                  />
                ))}
              </Select>
            </div>
          </div>

          {(studentSearchTerm || classSearchTerm || attendanceStatusFilter) &&
            visibleRevenues.length === 0 && (
              <p className="text-14 mt-20 text-grey">
                No students match your search.
              </p>
            )}

          <div style={{ overflow: 'auto' }} className="table-container mt-30">
            <table className="bx--data-table table-white">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Status</th>
                  <th>
                    Total arrears <br />
                    (GH₵)
                  </th>
                  <th>
                    Pay arrears <br />
                    (GH₵)
                  </th>
                  <th>
                    Deposit balance <br />
                    (GH₵)
                  </th>
                  <th>
                    Deposit allocated <br />
                    (GH₵)
                  </th>
                  <th>
                    Pay deposit <br />
                    (GH₵)
                  </th>
                  <th>
                    Amount Paid <br />
                    (GH₵)
                  </th>
                  <th>
                    Total payment <br />
                    (GH₵)
                  </th>
                  <th>Payment Mode/Source</th>
                </tr>
              </thead>
              <tbody>
                {visibleRevenues.map((entry) => (
                  <tr key={entry.studentId}>
                    <td>
                      {entry.firstName} {entry.lastName}
                    </td>
                    <td>{entry.class || entry.classDescription}</td>
                    <td>
                      <Select
                        style={{ minWidth: '130px' }}
                        labelText=""
                        value={entry.attendanceStatus}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          setHasChanged(true);
                          setFormData({
                            revenues: formData.revenues.map((ent) =>
                              ent.studentId === entry.studentId
                                ? {
                                    ...ent,
                                    attendanceStatus: newStatus,

                                    ...calculatePaymentBreakdown({
                                      totalPayment:
                                        newStatus === 'Absent'
                                          ? 0
                                          : entry.tempAmountPaid || 0,
                                      totalArrears: ent.totalArrears,
                                      currentAmount,
                                      depositBalance: ent.depositBalance,
                                      attendanceStatus: newStatus,
                                    }),
                                  }
                                : ent,
                            ),
                          });
                        }}
                      >
                        {makeDropdownOptions(['Present', 'Absent']).map(
                          (item) => (
                            <SelectItem
                              key={item.value}
                              text={item.label}
                              value={item.value}
                            />
                          ),
                        )}
                      </Select>
                    </td>
                    <td>{entry.totalArrears}</td>
                    <td>{entry.payArrears}</td>
                    <td>{entry.depositBalance}</td>
                    <td>
                      {entry.attendanceStatus === 'Present'
                        ? entry.depositPaymentAmount
                        : ''}
                    </td>
                    <td>{entry.payAdvance}</td>
                    <td>{entry.amountPaid}</td>
                    <td style={{ width: '160px' }}>
                      <TextInput
                        id={`totalPayment-${entry.studentId}`}
                        name={`totalPayment-${entry.studentId}`}
                        type="number"
                        labelText=""
                        placeholder=""
                        className="p-0"
                        disabled={entry.attendanceStatus === 'Absent'}
                        value={
                          entry.attendanceStatus === 'Absent'
                            ? ''
                            : entry.totalPayment
                        }
                        onChange={(e) => {
                          const totalPayment = Math.max(
                            0,
                            parseFloat(e.target.value) || 0,
                          );
                          setHasChanged(true);
                          setFormData({
                            revenues: formData.revenues.map((ent) =>
                              ent.studentId === entry.studentId
                                ? {
                                    ...ent,
                                    totalPayment,
                                    ...calculatePaymentBreakdown({
                                      totalPayment,
                                      totalArrears: ent.totalArrears,
                                      currentAmount,
                                      depositBalance: ent.depositBalance,
                                      attendanceStatus: ent.attendanceStatus,
                                    }),
                                  }
                                : ent,
                            ),
                          });
                        }}
                      />
                    </td>
                    <td style={{ width: '250px' }}>
                      <Select
                        style={{ minWidth: '130px' }}
                        id="paymentMode"
                        labelText=""
                        disabled={entry.attendanceStatus === 'Absent'}
                        value={
                          entry.attendanceStatus === 'Absent'
                            ? ''
                            : entry.paymentMode
                        }
                        onChange={(e) => {
                          const newMode = e.target.value;
                          setHasChanged(true);
                          setFormData({
                            revenues: formData.revenues.map((ent) =>
                              ent.studentId === entry.studentId
                                ? {
                                    ...ent,
                                    paymentMode:
                                      ent.attendanceStatus === 'Absent'
                                        ? ''
                                        : newMode,
                                  }
                                : ent,
                            ),
                          });
                        }}
                      >
                        <SelectItem disabled value="" text="" />
                        {OPTIONS.paymentModes
                          .filter((mode) => mode.value !== 'Cheque')
                          .map((mode) => (
                            <SelectItem
                              key={mode.value}
                              value={mode.value}
                              text={mode.label}
                            />
                          ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold">
                  <td colSpan="3">Total</td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.totalArrears || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.payArrears || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) =>
                        acc + parseFloat(entry.depositBalance || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) =>
                        acc + parseFloat(entry.depositPaymentAmount || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.payAdvance || 0),
                      0,
                    )}
                  </td>

                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.amountPaid || 0),
                      0,
                    )}
                  </td>
                  <td>
                    {formData.revenues.reduce(
                      (acc, entry) => acc + parseFloat(entry.totalPayment || 0),
                      0,
                    )}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {isSaved && (
            <div className="mt-20 text-success">Data saved as draft</div>
          )}

          <div className="mt-80">
            <p className="text-12" style={{ justifySelf: 'end' }}>
              Please "Save" changes as draft before you "SUBMIT"!
            </p>
            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                gap: '10px',
                justifyContent: 'end',
              }}
            >
              <CustomButton
                type="submit"
                classes={`secondary font-bold`}
                isLoading={isMutating}
                onClick={handleSubmit}
                disabled={
                  !formData.revenues.length ||
                  formData.revenues.some(
                    (entry) =>
                      !entry.studentId ||
                      // entry.amountPaid === '' ||
                      Number(entry.amountPaid) < 0 ||
                      !entry.attendanceStatus,
                  ) ||
                  // if amountPaid is more than zero, payment mode is required
                  formData.revenues.some(
                    (entry) =>
                      entry.attendanceStatus === 'Present' &&
                      Number(entry.amountPaid) > 0 &&
                      !entry.paymentMode,
                  ) ||
                  isSubmitting ||
                  !hasChanged
                }
              >
                Save AS DRAFT
              </CustomButton>
              <CustomButton
                type="button"
                classes={`primary font-bold`}
                disabled={isMutating || hasChanged}
                onClick={() => setIsOpen('submit')}
              >
                SUBMIT
              </CustomButton>
            </div>
          </div>
        </form>
      )}

      {additionalStudents.length > 0 && (
        <AdditionalStudents
          students={additionalStudents}
          formDataRevenues={formData.revenues}
          setFormData={setFormData}
          currentAmount={currentAmount}
        />
      )}

      <ConfirmModal
        title="Are you sure you want to sumit?"
        isOpen={isOpen === 'submit'}
        closeModal={() => setIsOpen('')}
        description={
          <>
            <p className="text-label">
              Are you very sure you want to submit this collection? You can not
              edit it after submission.
            </p>
          </>
        }
        actionText="SUBMIT"
        onClick={() =>
          submit(
            {
              ...formData,
              paymentDate: urlPaymentDate,
              ...(urlGenericGroupingId
                ? { genericGroupingId: parseFloat(urlGenericGroupingId) }
                : {}),
              ...(urlClassId ? { classId: urlClassId } : {}),
              dailyBillingFeeId: parseFloat(urlDailyBillingFeeId),
              debitAccountId: responseData?.debitAccount?.id,
            },
            {
              onSuccess: () => {
                history.push(
                  `/daily-fees?${getQueryString({
                    paymentDate: urlPaymentDate,
                    classId: urlClassId,
                    genericGroupingId: urlGenericGroupingId,
                    dailyBillingFeeId: urlDailyBillingFeeId,
                  })}`,
                );
              },
            },
          )
        }
        // disabled={!agreeToTerms}
        isLoading={isSubmitting}
        errorMessage={submissionError ? submissionError.message : null}
        actionBtnClasses="primary"
      />
    </PageLayout>
  );
}
